import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  className?: string;
}

/**
 * Accessible modal using a backdrop div, PORTALED to document.body.
 *
 * Why the portal is load-bearing: the modal used to render inline wherever
 * it was mounted. Ancestors that create a stacking context (e.g. the
 * position:sticky sidebar) then confine the backdrop's z-index:1000 to that
 * context — the sidebar itself has z-index:auto, so page content (library
 * rows) painted ON TOP of the dialog and intercepted pointer events.
 * Portaling to document.body puts the backdrop in the ROOT stacking
 * context, where z-index:1000 is above everything else in the app
 * (next-highest is the mobile drawer at 200). Do not remove the portal.
 *
 * - Esc key closes
 * - Backdrop click closes
 * - Basic focus trap: first focusable element gets focus on open
 */
export function Modal({ open, onClose, children, className = "" }: ModalProps) {
  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        // Close only when clicking the backdrop itself, not the panel
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`modal-panel ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
